import type { Article, CreativeWork, Product, Service } from 'schema-dts';

import type {
  ArticleSchemaInput,
  CreativeWorkSchemaInput,
  ItemListSchemaConfig,
  NonStringThing,
  ProductSchemaInput,
  SchemaInput,
  SchemaOutput,
  SchemaType,
  ServiceSchemaInput,
} from './structuredData.types';
import {
  STORYBLOCKS_ORG,
  STORYBLOCKS_PUBLISHER_REF,
} from './structuredDataConstants';

const stripHtmlTags = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

const validateSchemaOutput = (schema: SchemaOutput): void => {
  if (process.env.NODE_ENV !== 'production') {
    if (!schema['@type']) {
      console.warn(
        'Generated schema object missing required @type property:',
        schema
      );
    }

    if (schema['@type'] === 'Product' && !schema.name) {
      console.warn('Product schema missing required name property:', schema);
    }

    if (schema['@type'] === 'Service' && !schema.name) {
      console.warn('Service schema missing required name property:', schema);
    }

    if (schema['@type'] === 'Article' && !schema.name) {
      console.warn('Article schema missing required name property:', schema);
    }

    if (schema['@type'] === 'CreativeWork' && !schema.name) {
      console.warn(
        'CreativeWork schema missing required name property:',
        schema
      );
    }
  }
};

const getOrganizationReference = (organizationName?: string) => {
  if (organizationName === 'Storyblocks') {
    return STORYBLOCKS_ORG;
  }
  return organizationName
    ? {
        '@type': 'Organization' as const,
        name: organizationName,
      }
    : undefined;
};

const getPublisherReference = (organizationName?: string) => {
  if (organizationName === 'Storyblocks') {
    return STORYBLOCKS_PUBLISHER_REF.publisher;
  }
  return organizationName
    ? {
        '@type': 'Organization' as const,
        name: organizationName,
      }
    : undefined;
};

const generateProductItem = (item: ProductSchemaInput): Product => {
  const product: Product = {
    '@type': 'Product',
    name: item.title,
    ...(item.description && { description: stripHtmlTags(item.description) }),
    ...(item.image && { image: item.image }),
    ...(item.url && { url: item.url }),
  };

  if (item.price && item.currency) {
    product.offers = {
      '@type': 'Offer',
      price: item.price,
      priceCurrency: item.currency,
    };
  }

  validateSchemaOutput(product);
  return product;
};

const generateServiceItem = (
  item: ServiceSchemaInput,
  organizationName?: string
): Service => {
  const service: Service = {
    '@type': 'Service',
    name: item.title,
    ...(item.description && { description: stripHtmlTags(item.description) }),
    ...(item.image && { image: item.image }),
    ...(item.url && { url: item.url }),
  };

  const organization = getOrganizationReference(organizationName);
  if (organization) {
    service.provider = organization;
  }

  if (item.price && item.currency) {
    service.offers = {
      '@type': 'Offer',
      price: item.price,
      priceCurrency: item.currency,
    };
  }

  validateSchemaOutput(service);
  return service;
};

const generateArticleItem = (
  item: ArticleSchemaInput,
  organizationName?: string
): Article => {
  const article: Article = {
    '@type': 'Article',
    name: item.title,
    headline: item.headline || item.title,
    ...(item.description && { description: stripHtmlTags(item.description) }),
    ...(item.image && { image: item.image }),
    ...(item.url && { url: item.url }),
  };

  const publisher = getPublisherReference(organizationName);
  if (publisher) {
    article.publisher = publisher;
  }

  validateSchemaOutput(article);
  return article;
};

const generateCreativeWorkItem = (
  item: CreativeWorkSchemaInput,
  organizationName?: string
): CreativeWork => {
  const creativeWork: CreativeWork = {
    '@type': 'CreativeWork',
    name: item.title,
    ...(item.description && { description: stripHtmlTags(item.description) }),
    ...(item.image && { image: item.image }),
    ...(item.url && { url: item.url }),
  };

  const organization = getOrganizationReference(organizationName);
  if (organization) {
    creativeWork.creator = organization;
  }

  validateSchemaOutput(creativeWork);
  return creativeWork;
};

const generateSchemaItem = (
  schemaType: SchemaType,
  item: SchemaInput,
  organizationName?: string
): SchemaOutput => {
  switch (schemaType) {
    case 'product':
      return generateProductItem(item as ProductSchemaInput);
    case 'service':
      return generateServiceItem(item as ServiceSchemaInput, organizationName);
    case 'article':
      return generateArticleItem(item as ArticleSchemaInput, organizationName);
    case 'creative-work':
      return generateCreativeWorkItem(
        item as CreativeWorkSchemaInput,
        organizationName
      );
    default:
      throw new Error(`Unsupported schema type: ${schemaType}`);
  }
};

/**
 * Generate ItemList structured data for various content types
 *
 * @param config Configuration object containing schema type, items, and metadata
 * @returns Structured data object ready for JSON-LD serialization, or null if no items
 */
export const generateItemListSchema = <T extends SchemaInput>(
  config: ItemListSchemaConfig<T>
): NonStringThing | null => {
  const { schemaType, heading, description, items, url, organizationName } =
    config;

  if (!items?.length) {
    return null;
  }

  const itemListElements = items.map((item) =>
    generateSchemaItem(schemaType, item, organizationName)
  );

  const itemList: NonStringThing = {
    '@type': 'ItemList',
    name: heading,
    ...(description && { description: stripHtmlTags(description) }),
    numberOfItems: items.length,
    ...(url && { url }),
    itemListElement: itemListElements,
  };

  return itemList;
};
